import { computed, nextTick, onBeforeUnmount, ref, watch, type Ref } from 'vue'
import type { ComponentPublicInstance } from 'vue'

type UseMeasuredVirtualListOptions<T> = {
  items: Ref<T[]>
  scrollRef: Ref<HTMLElement | null>
  getItemId: (item: T) => number
  estimatedItemHeight: (item: T) => number
  overscanPx?: number
}

export function useMeasuredVirtualList<T>({
  items,
  scrollRef,
  getItemId,
  estimatedItemHeight,
  overscanPx = 800
}: UseMeasuredVirtualListOptions<T>) {
  const scrollTop = ref(0)
  const viewportHeight = ref(0)
  const heightById = ref<Record<number, number>>({})

  const resizeObservers = new Map<number, ResizeObserver>()

  let pendingHeightUpdates: Record<number, number> = {}
  let heightFlushFrame: number | null = null
  const elementById = new Map<number, HTMLElement>()

  function syncViewportHeight() {
    viewportHeight.value = scrollRef.value?.clientHeight ?? 0
  }

  function resetScroll() {
    scrollTop.value = 0

    if (scrollRef.value) {
      scrollRef.value.scrollTop = 0
    }
  }

  function onScroll(event: Event) {
    scrollTop.value = (event.target as HTMLElement).scrollTop
  }

  function setScrollTop(top: number) {
  const nextTop = Math.max(0, top)

  scrollTop.value = nextTop

  if (scrollRef.value) {
    scrollRef.value.scrollTop = nextTop
  }
}

  function flushPendingHeightUpdates() {
    heightFlushFrame = null

    if (!Object.keys(pendingHeightUpdates).length) {
      return
    }

    const prevScrollTop = scrollTop.value

    const nextHeights = {
      ...heightById.value,
      ...pendingHeightUpdates
    }

    let running = 0
    let deltaAbove = 0

    for (let i = 0; i < items.value.length; i++) {
      const item = items.value[i]
      const id = getItemId(item)

      const prevHeight =
        heightById.value[id] ?? estimatedItemHeight(item)

      const nextHeight =
        nextHeights[id] ?? estimatedItemHeight(item)

      // if this item starts above the viewport
      if (running < prevScrollTop) {
        deltaAbove += (nextHeight - prevHeight)
      }

      running += nextHeight
    }

    heightById.value = nextHeights
    pendingHeightUpdates = {}

    if (deltaAbove !== 0 && scrollRef.value) {
      const nextScroll = prevScrollTop + deltaAbove

      scrollTop.value = nextScroll
      scrollRef.value.scrollTop = nextScroll
    }
  }

  function queueHeightUpdate(itemId: number, height: number) {
    if (!Number.isFinite(height) || height <= 0) return
    if (heightById.value[itemId] === height && pendingHeightUpdates[itemId] == null) return

    pendingHeightUpdates[itemId] = height

    if (heightFlushFrame != null) {
      return
    }

    heightFlushFrame = window.requestAnimationFrame(() => {
      flushPendingHeightUpdates()
    })
  }

  function setItemElement(
    itemId: number,
    el: Element | ComponentPublicInstance | null
  ) {
    const existingObserver = resizeObservers.get(itemId)

    if (!el) {
      if (existingObserver) {
        existingObserver.disconnect()
        resizeObservers.delete(itemId)
      }

      elementById.delete(itemId)
      delete pendingHeightUpdates[itemId]
      return
    }

    const element = el as HTMLElement
    elementById.set(itemId, element)

    if (existingObserver) {
      existingObserver.disconnect()
    }

    queueHeightUpdate(itemId, Math.ceil(element.getBoundingClientRect().height))

    const observer = new ResizeObserver(entries => {
      const entry = entries[0]

      if (!entry) return

      queueHeightUpdate(itemId, Math.ceil(entry.contentRect.height))
    })

    observer.observe(element)
    resizeObservers.set(itemId, observer)
  }

  function getItemElement(itemId: number) {
    return elementById.get(itemId) ?? null
  }

  const prefixHeights = computed(() => {
    const offsets: number[] = []
    let running = 0

    for (const item of items.value) {
      const itemId = getItemId(item)
      offsets.push(running)
      running += heightById.value[itemId] ?? estimatedItemHeight(item)
    }

    return {
      offsets,
      total: running
    }
  })

  const totalHeight = computed(() => prefixHeights.value.total)

  function findStartIndex(targetTop: number) {
    const { offsets } = prefixHeights.value
    let low = 0
    let high = offsets.length - 1
    let answer = 0

    while (low <= high) {
      const mid = Math.floor((low + high) / 2)

      if (offsets[mid] <= targetTop) {
        answer = mid
        low = mid + 1
      } else {
        high = mid - 1
      }
    }

    return answer
  }

  const startIndex = computed(() => {
    const target = Math.max(0, scrollTop.value - overscanPx)
    return findStartIndex(target)
  })

  const endIndex = computed(() => {
    const targetBottom = scrollTop.value + viewportHeight.value + overscanPx
    const { offsets } = prefixHeights.value

    let index = startIndex.value

    while (index < items.value.length) {
      const item = items.value[index]
      const itemId = getItemId(item)
      const top = offsets[index]
      const height = heightById.value[itemId] ?? estimatedItemHeight(item)

      if (top > targetBottom) {
        break
      }

      if (top + height >= targetBottom) {
        return index + 1
      }

      index++
    }

    return items.value.length
  })

  const offsetTop = computed(() =>
    prefixHeights.value.offsets[startIndex.value] ?? 0
  )

  const renderedItems = computed(() =>
    items.value.slice(startIndex.value, endIndex.value)
  )

  watch(items, async () => {
    await nextTick()
    syncViewportHeight()
  })

  onBeforeUnmount(() => {
    if (heightFlushFrame != null) {
      window.cancelAnimationFrame(heightFlushFrame)
    }

    for (const observer of resizeObservers.values()) {
      observer.disconnect()
    }

    resizeObservers.clear()
    elementById.clear()
    pendingHeightUpdates = {}
  })

  return {
    totalHeight,
    offsetTop,
    renderedItems,
    syncViewportHeight,
    resetScroll,
    onScroll,
    setScrollTop,
    setItemElement,
    getItemElement
  }
}